import { PULSE_COLORS } from './pulseConfig.js'
import { colorFromName, getInitials } from './pulseHelpers.js'

const BAR_COLORS = ['#0891B2', '#4F46E5', '#059669', '#D97706', '#DB2777', '#7C3AED', '#DC2626', '#2563EB']

function formatCount(value) {
    return new Intl.NumberFormat('en-US').format(Number(value) || 0)
}

function Metric({ label, value, color, bg, loading }) {
    return (
        <div style={{ minHeight: '96px', textAlign: 'center' }}>
            {loading ? (
                <div
                    style={{
                        width: '80px',
                        height: '36px',
                        margin: '0 auto',
                        borderRadius: '6px',
                        background: PULSE_COLORS.border,
                    }}
                />
            ) : (
                <div style={{ fontSize: '30px', fontWeight: 700, color: PULSE_COLORS.text }}>{value}</div>
            )}
            <span
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    marginTop: '8px',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    fontSize: '11px',
                    fontWeight: 700,
                    background: bg,
                    color,
                }}
            >
                {label}
            </span>
        </div>
    )
}

export default function AiAutomationInsights({
    aiCompletedTests,
    nonAiCompletedTests,
    totalTests,
    aiUsagePct,
    authors = [],
    loading,
    headerAction,
}) {
    const ranked = [...authors]
        .filter((author) => author.qaTeam && author.aiCompletedTests > 0)
        .sort((a, b) => b.aiCompletedTests - a.aiCompletedTests || a.name.localeCompare(b.name))
    const top = ranked.slice(0, 8)
    const max = top[0]?.aiCompletedTests ?? 1

    return (
        <div
            style={{
                background: PULSE_COLORS.cardBg,
                borderRadius: '12px',
                padding: '20px',
                border: `1px solid ${PULSE_COLORS.border}`,
                height: '100%',
            }}
        >
            <div style={{ marginBottom: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <h3 style={{ color: PULSE_COLORS.text, fontWeight: 600, fontSize: '16px', margin: 0 }}>
                        AI Adoption
                    </h3>
                    {headerAction}
                </div>
                <p style={{ color: PULSE_COLORS.textMuted, fontSize: '13px', margin: '6px 0 0' }}>
                    Tracks repo-wide @aiCompleted coverage and attributed test authorship. AI-assisted tagging started
                    in November 2025.
                </p>
            </div>

            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
                    gap: '16px',
                    marginBottom: '20px',
                }}
            >
                <Metric
                    label="AICOMPLETED TESTS"
                    value={formatCount(aiCompletedTests)}
                    color="#67E8F9"
                    bg="#164E63"
                    loading={loading}
                />
                <Metric label="AI USAGE" value={`${aiUsagePct}%`} color="#6EE7B7" bg="#064E3B" loading={loading} />
                <Metric
                    label="NON-AI TESTS"
                    value={formatCount(nonAiCompletedTests)}
                    color="#FCD34D"
                    bg="#78350F"
                    loading={loading}
                />
                <Metric
                    label="TOTAL TESTS"
                    value={formatCount(totalTests)}
                    color={PULSE_COLORS.textMuted}
                    bg={PULSE_COLORS.bgInner}
                    loading={loading}
                />
            </div>

            <div style={{ paddingTop: '16px', borderTop: `1px solid ${PULSE_COLORS.border}` }}>
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        gap: '12px',
                        marginBottom: '12px',
                    }}
                >
                    <div>
                        <p
                            style={{
                                color: PULSE_COLORS.textMuted,
                                fontSize: '11px',
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                letterSpacing: '0.08em',
                                margin: 0,
                            }}
                        >
                            AICompleted Test Authors
                        </p>
                        <p style={{ color: PULSE_COLORS.textMuted, fontSize: '12px', margin: '4px 0 0' }}>
                            Current default branch, attributed from GitLab blame.
                        </p>
                    </div>
                    {ranked.length > 0 && (
                        <span style={{ color: PULSE_COLORS.textMuted, fontSize: '12px', fontWeight: 700 }}>
                            {ranked.length} contributor{ranked.length === 1 ? '' : 's'}
                        </span>
                    )}
                </div>

                {loading && top.length === 0 ? (
                    <div style={{ display: 'grid', gap: '10px' }}>
                        {[...Array(4)].map((_, i) => (
                            <div
                                key={i}
                                style={{ height: '36px', borderRadius: '6px', background: PULSE_COLORS.border }}
                            />
                        ))}
                    </div>
                ) : top.length === 0 ? (
                    <p style={{ color: PULSE_COLORS.textMuted, fontSize: '13px', margin: 0 }}>
                        No attributed @aiCompleted tests found on the default branch.
                    </p>
                ) : (
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: '12px' }}>
                        {top.map((member) => {
                            const color = colorFromName(member.name, BAR_COLORS)
                            const widthPct = Math.max(8, Math.round((member.aiCompletedTests / max) * 100))
                            return (
                                <li
                                    key={member.name}
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns: '36px minmax(0, 1fr) auto',
                                        alignItems: 'center',
                                        gap: '12px',
                                    }}
                                >
                                    <span
                                        style={{
                                            width: '36px',
                                            height: '36px',
                                            borderRadius: '50%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: '#fff',
                                            fontSize: '12px',
                                            fontWeight: 700,
                                            background: color,
                                        }}
                                    >
                                        {getInitials(member.name)}
                                    </span>
                                    <div style={{ minWidth: 0 }}>
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                gap: '12px',
                                            }}
                                        >
                                            <span
                                                style={{
                                                    color: PULSE_COLORS.text,
                                                    fontSize: '13px',
                                                    fontWeight: 600,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                {member.name}
                                            </span>
                                            <span
                                                style={{
                                                    color: PULSE_COLORS.textMuted,
                                                    fontSize: '12px',
                                                    fontWeight: 700,
                                                }}
                                            >
                                                {member.aiCompletedTests}
                                            </span>
                                        </div>
                                        <div
                                            style={{
                                                height: '8px',
                                                marginTop: '4px',
                                                background: PULSE_COLORS.bgInner,
                                                borderRadius: '999px',
                                                overflow: 'hidden',
                                            }}
                                        >
                                            <div
                                                style={{
                                                    height: '100%',
                                                    width: `${widthPct}%`,
                                                    background: color,
                                                    borderRadius: '999px',
                                                }}
                                            />
                                        </div>
                                    </div>
                                    <span
                                        style={{
                                            color: PULSE_COLORS.textSubtle,
                                            fontSize: '11px',
                                            fontWeight: 700,
                                            textTransform: 'uppercase',
                                        }}
                                    >
                                        tests
                                    </span>
                                </li>
                            )
                        })}
                    </ul>
                )}
            </div>
        </div>
    )
}
